import React from 'react';
import { ColorValue, View } from 'react-native';
import { faBasketShopping, faReceipt } from '@fortawesome/pro-regular-svg-icons';
import useTranslation from '../../../hooks/translation-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import CustomMarkdown from '../CustomMarkDown';
import MoreInfoButton, { MoreInfoButtonProps } from '../MoreInfoButton';
import { getMissionReimbursementHelp } from '../../../helpers/missionCompensation';
import MissionCompensationRow from './MissionCompensationRow';

export interface MissionPurchaseConditionsValue {
  requiredPurchase?: boolean;
  requiredPurchaseText?: string;
  requiredPurchaseExclusionText?: string;
  reimbursementValue?: string;
  reimbursementMaxAmount?: number;
  reimbursementMaxPoints?: number;
  totalPaymentMaxAmount?: number | null;
}

interface MissionPurchaseConditionsProps {
  conditions: MissionPurchaseConditionsValue;
  settled?: boolean;
  showReimbursementPoints?: boolean;
  textColor?: ColorValue;
  iconColor?: ColorValue;
  helpColor?: string;
  moreInfoButtonProps?: Pick<MoreInfoButtonProps, 'suspendDrawerOnOpen' | 'useLightBoxOnNative' | 'onOpen'>;
}

const MissionPurchaseConditions = ({ conditions, settled = false, showReimbursementPoints = false,
  textColor, iconColor, helpColor, moreInfoButtonProps }: MissionPurchaseConditionsProps) => {
  const t = useTranslation();
  const uiLanguage = useAppSelector(state => state.userPrefs.uiLanguage);
  const currency = (amount: number) => new Intl.NumberFormat(uiLanguage?.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    style: 'currency', currency: 'CAD',
  }).format(amount);
  const help = getMissionReimbursementHelp(conditions.reimbursementValue ?? '', conditions.reimbursementMaxAmount, uiLanguage);
  const helpText = t(help.key, help.values);

  const value = conditions.reimbursementValue ?? '';
  const heading = settled
    ? `${t('reimbursement')}: ${value}`
    : `${t('reimbursement-up-to')} ${value}`;
  const caption = !settled && conditions.reimbursementMaxAmount > 0
    ? t('at-home-reimbursement-cap', { amount: currency(conditions.reimbursementMaxAmount) })
      + (showReimbursementPoints && conditions.reimbursementMaxPoints !== undefined ? ` (${conditions.reimbursementMaxPoints} pts)` : '')
    : undefined;

  return <>
    {conditions.requiredPurchase && <MissionCompensationRow icon={faBasketShopping} separator
      heading={t('purchase_required')} color={textColor} iconColor={iconColor} />}
    {!!value && <MissionCompensationRow icon={faReceipt} separator heading={heading} caption={caption}
      color={textColor} iconColor={iconColor}
      help={help.visible ? <MoreInfoButton {...moreInfoButtonProps}
        style={{ width: 32, height: 32, marginTop: -5, justifyContent: 'center' }} title="" color={helpColor}>
        {helpText === help.key ? t(help.fallbackKey) : helpText}
      </MoreInfoButton> : undefined} />}
  </>;
};

export const MissionPurchaseDetails = ({ conditions }: { conditions: MissionPurchaseConditionsValue }) => {
  if (!conditions.requiredPurchaseText && !conditions.requiredPurchaseExclusionText) return null;
  return <View testID="mission-purchase-details" style={{ width: '100%', minWidth: 0, paddingHorizontal: 12, marginBottom: 16, gap: 8 }}>
    {!!conditions.requiredPurchaseText && <CustomMarkdown>{conditions.requiredPurchaseText}</CustomMarkdown>}
    {!!conditions.requiredPurchaseExclusionText && <CustomMarkdown>{conditions.requiredPurchaseExclusionText}</CustomMarkdown>}
  </View>;
};

export default MissionPurchaseConditions;
